import {Injectable} from '@angular/core';
import {WorkInfoModel} from '../../models/work-info/work-info.model';
import {OrderItemsService} from '../entities/order-items/order-items.service';
import {applyTransaction} from '@datorama/akita';
import {InitialWorkInfoModel} from '../../models/work-info/initial-work-info.model';
import {WorkInfoOrderChangedModel} from '../../models/work-info/work-info-order-changed.model';
import {WorkInfoAbsenceChangedModel} from '../../models/work-info/work-info-absence-changed.model';
import {WorkInfoCostCenterChangedModel} from '../../models/work-info/work-info-cost-center-changed.model';
import { WorkInfoOperationChangedModel } from '../../models/work-info/work-info-operation-changed.model';
import { WorkInfoBackendService } from '../../services/work-info/work-info-backend.service';
import { AbsenceItemsQuery } from '../entities/absence-items/absence-items.query';
import { SessionQuery } from '../session/session.query';
import { LocalStateKeys } from '../local-state-keys';
import { WorkInfoStoreService } from '../work-info-store-service';
import { OrderItemsStoreService } from '../order-items-store-service';
import { CostCenterItemsStoreService } from '../cost-center-items-store-service';
import { OperationItemsStoreService } from '../operation-items-store-service';
import { WorkInfoStore } from './work-info.store';
import { LocalStoreConfigModel } from '../../models/local-store-config.model';
import { CostCenterItemsService } from '../entities/cost-center-items/cost-center-items.service';
import { OperationItemsService } from '../entities/operation-items/operation-items.service';
import { OperationStateEnum, WorkInfoBasic } from '../../models/client-models';

@Injectable({ providedIn: 'root' })
export class WorkInfoService {
  constructor(
    private workInfoStoreService: WorkInfoStoreService,
    private orderItemsService: OrderItemsService,
    private workInfoBackendService: WorkInfoBackendService,
    private absenceItemsQuery: AbsenceItemsQuery,
    private orderItemsStoreService: OrderItemsStoreService,
    private costCenterItemsStoreService: CostCenterItemsStoreService,
    private operationItemsStoreService: OperationItemsStoreService,
    private costCenterItemsService: CostCenterItemsService,
    private operationItemsService: OperationItemsService,
    private sessionQuery: SessionQuery
  ) {}

  /**
   * Update the work information.
   */
  updateWorkInfo(
    data: WorkInfoModel,
    storeKey: LocalStateKeys,
    orderItemsStoreKey: LocalStateKeys
  ) {
    const orderStore =
      this.orderItemsStoreService.getOrCreateStore(orderItemsStoreKey);
    const store = this.workInfoStoreService.getOrCreateStore(storeKey);

    applyTransaction(() => {
      store.store.update((state) => ({
        ...state,
        workInfo: data,
      }));
      if (
        data.order?.order?.orderID &&
        orderStore.query.getActive()?.orderID !== data.order.order.orderID
      ) {
        this.orderItemsService.setOrder(data.order.order, orderItemsStoreKey);
      }
    });
  }

  disableCostCenterAndOperationSelectable(storeKey: LocalStateKeys) {
    const store = this.workInfoStoreService.getOrCreateStore(storeKey);
    store.store.update((state) => ({
      ...state,
      workInfo: {
        ...state.workInfo,
        operation: {
          ...state.workInfo.operation,
          state: OperationStateEnum.Selectable,
        },
        costCenter: {
          ...state.workInfo.costCenter,
          selectable: false,
        },
      },
    }));
  }

  /**
   * Update store with operation text.
   *
   * @param text new text for operation.
   * @param storeKey
   */
  setOperationText(text: string, storeKey: LocalStateKeys) {
    const store = this.workInfoStoreService.getOrCreateStore(storeKey);

    store.store.update((state) => ({
      ...state,
      workInfo: {
        ...state.workInfo,
        operation: {
          ...state.workInfo.operation,
          text,
          shouldBeSelectedNext: false,
        },
      },
    }));
  }

  /**
   * Set work info to undefined.
   */
  clearWorkInfo(storeKey: LocalStateKeys, orderItemsStoreKey: LocalStateKeys) {
    this.updateWorkInfo({} as WorkInfoModel, storeKey, orderItemsStoreKey);
  }

  setAbsenceSelectable(storeKey: LocalStateKeys) {
    const store = this.workInfoStoreService.getOrCreateStore(storeKey);
    store.store.update((state) => ({
      ...state,
      workInfo: {
        ...state.workInfo,
        absence: {
          ...state.workInfo.absence,
          selectable: true,
        },
      },
    }));
  }

  /**
   * Get initial work info state from backend
   */
  getInitialWorkInfoState(
    storeKey: LocalStateKeys,
    orderItemsStoreKey: LocalStateKeys,
    workInfo?: WorkInfoModel,
    personId?: number
  ) {
    const info = workInfo ? workInfo : ({} as WorkInfoModel);

    if (!personId) {
      personId = this.sessionQuery.getValue().user?.id;
    }

    const workRequest = {
      WorkInfo: info,
      PersonID: personId,
    } as InitialWorkInfoModel;
    this.workInfoBackendService
      .getInitialState(workRequest)
      .subscribe((data) => {
        this.updateWorkInfo(data, storeKey, orderItemsStoreKey);
      });
  }

  /**
   * New Order selected
   */
  newOrderSelected(
    storeKey: LocalStateKeys,
    orderItemsStoreKey: LocalStateKeys
  ) {
    const store =
      this.orderItemsStoreService.getOrCreateStore(orderItemsStoreKey);
    const workInfoStore = this.workInfoStoreService.getOrCreateStore(storeKey);

    const request = {
      OldState: workInfoStore.query.getValue().workInfo,
      PersonID: this.sessionQuery.getValue().user?.id,
      NewOrderID: store.query.getActive()?.orderID,
    } as WorkInfoOrderChangedModel;

    this.workInfoBackendService.newOrderSelected(request).subscribe((data) => {
      this.updateWorkInfo(data, storeKey, orderItemsStoreKey);
    });
  }

  newAbsenceSelected(
    storeKey: LocalStateKeys,
    orderItemsStoreKey: LocalStateKeys
  ) {
    const store = this.workInfoStoreService.getOrCreateStore(storeKey);
    const request = {
      OldState: store.query.getValue().workInfo,
      PersonID: this.sessionQuery.getValue().user?.id,
      AbsenceID: this.absenceItemsQuery.getActive()?.id,
    } as WorkInfoAbsenceChangedModel;

    this.workInfoBackendService
      .newAbsenceSelected(request)
      .subscribe((data) => {
        this.updateWorkInfo(data, storeKey, orderItemsStoreKey);
      });
  }

  newCostCenterSelected(
    storeKey: LocalStateKeys,
    orderItemsStoreKey: LocalStateKeys,
    costCenterStoreKey: LocalStateKeys
  ) {
    const store = this.workInfoStoreService.getOrCreateStore(storeKey);
    const costCenterStore =
      this.costCenterItemsStoreService.getOrCreateStore(costCenterStoreKey);

    const request = {
      OldState: store.query.getValue().workInfo,
      PersonID: this.sessionQuery.getValue().user?.id,
      NewCostCenterID: costCenterStore.query.getActive()?.id,
    } as WorkInfoCostCenterChangedModel;

    this.workInfoBackendService
      .newCostCenterSelected(request)
      .subscribe((data) => {
        this.updateWorkInfo(data, storeKey, orderItemsStoreKey);
      });
  }

  newOperationSelected(
    storeKey: LocalStateKeys,
    orderItemsStoreKey: LocalStateKeys,
    operationStoreKey: LocalStateKeys
  ) {
    const store = this.workInfoStoreService.getOrCreateStore(storeKey);
    const operationStore =
      this.operationItemsStoreService.getOrCreateStore(operationStoreKey);

    const request = {
      OldState: store.query.getValue().workInfo,
      PersonID: this.sessionQuery.getValue().user?.id,
      NewOperationAksID: operationStore.query.getActive()?.aksPlanID,
      OrderID: store.query.getValue().workInfo?.order?.order?.orderID,
    } as WorkInfoOperationChangedModel;

    this.workInfoBackendService
      .newOperationSelected(request)
      .subscribe((data) => {
        this.updateWorkInfo(data, storeKey, orderItemsStoreKey);
      });
  }

  /**
   * Get work info for a changed time entry.
   *
   * @param work info.
   * @param storeKey
   * @param orderItemsStoreKey
   */
  getWorkInfoForChange(
    work: WorkInfoBasic,
    storeKey: LocalStateKeys,
    orderItemsStoreKey: LocalStateKeys
  ): void {
    this.workInfoBackendService.getWorkInfoChange(work).subscribe((data) => {
      this.updateWorkInfo(data, storeKey, orderItemsStoreKey);
    });
  }

  getWorkInfoFromBarcode(
    barcode: string,
    storeKey: LocalStateKeys,
    orderItemsStoreKey: LocalStateKeys
  ) {
    const store = this.workInfoStoreService.getOrCreateStore(storeKey);
    store.store.setLoading(true);
    applyTransaction(() => {
      this.workInfoBackendService
        .getWorkInfoBarcode(barcode, this.sessionQuery.getPersonID())
        .subscribe((data) => {
          this.updateWorkInfo(data, storeKey, orderItemsStoreKey);
          store.store.setLoading(false);
        });
    });
  }

  purgeStore(storeConfig: LocalStoreConfigModel) {
    const storeItems = this.workInfoStoreService.getOrCreateStore(
      storeConfig.workInfoItems
    );
    const store = storeItems.store as WorkInfoStore;

    store.reset();
    this.orderItemsService.clearCache(storeConfig.orderItems);

    if (storeConfig.costCenterItems) {
      this.costCenterItemsService.clearCache(storeConfig.orderItems);
    }

    if (storeConfig.operationItems) {
      this.operationItemsService.clearCache(storeConfig.orderItems);
    }
  }
}
